import { useLocalization } from "@fluent/react";
import { Button, formatNumber, Row } from "@narmi/design_system";
import ApiHttp from "byzantine/src/ApiHttp";
import utils from "byzantine/src/utils";
import { useFormData, ContextForm, useNotificationContext } from "cerulean";
import React, { useCallback } from "react";
import Account from "byzantine/src/Account";
import AmountTextInput from "../../form/AmountTextInput";
import azulUtils from "../../../utils";
import styles from "./VerifyAccount.module.scss";

interface VerifyAccountProps {
  account: Account;
  callback: (account: API.ExternalAccount) => void;
  uatMicrodepAmntOne?: string;
  uatMicrodepAmntTwo?: string;
  onCancel?: () => void;
}

interface VerifyResponse {
  accounts: any[];
}

interface ApiError {
  amounts?: string[];
  message?: string;
}

const VerifyAccount: React.FC<VerifyAccountProps> = ({
  account,
  callback,
  uatMicrodepAmntOne,
  uatMicrodepAmntTwo,
  onCancel
}) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { formData, setFormData, onChange } = useFormData({});

  const closeDialog = (): void => {
    setFormData({});
    onCancel?.();
  };

  const submitMicroDeposit = (formCallback: (error?: string) => void): void => {
    if (!formData.deposit1 || !formData.deposit2) {
      formCallback("Both deposit amounts are required");
      return;
    }

    const amount1 = utils.dollarsToPennies(
      formData.deposit1.replace(/[^\d.]/g, "")
    );
    const amount2 = utils.dollarsToPennies(
      formData.deposit2.replace(/[^\d.]/g, "")
    );

    ApiHttp.fetch<VerifyResponse>(
      `accounts/${account.id}/verify/`,
      {
        method: "POST"
      },
      {
        amounts: [amount1, amount2]
      }
    )
      .then((response) => {
        const updatedAccount = response.accounts[0];
        formCallback();
        callback(updatedAccount);
        closeDialog();
        sendNotification({
          type: "success",
          text: l10n.getString("external-account-verify-success")
        });
      })
      .catch((error: ApiError) => {
        formCallback(
          error?.amounts?.[0] ||
            azulUtils.extractErrorMessage(
              error,
              l10n.getString(
                "error-generic",
                null,
                l10n.getString("error-unknown")
              )
            )
        );
      });
  };

  const handleSubmit = useCallback(
    (formCallback: (error?: string) => void) => {
      submitMicroDeposit(formCallback);
    },
    [submitMicroDeposit]
  );

  return (
    <>
      {/* Header with FI logo, title, and account details */}
      <div className={styles.header}>
        <Row alignItems="center" gapSize="m">
          <Row.Item shrink>
            {account.fi_svg ? (
              <img
                alt="bank logo"
                className={styles.logoContainer}
                src={`data:image/svg+xml;utf8,${encodeURIComponent(
                  account.fi_svg
                )}`}
              />
            ) : (
              <div className="generic-bank-logo">
                <span className="narmi-icon-bank" />
              </div>
            )}
          </Row.Item>
          <Row.Item>
            <div className={styles.title}>
              {l10n.getString(
                "external-account-verify-title",
                null,
                "Verify your linked account"
              )}
            </div>
            <div className={styles.accountDetails}>
              {account.fi_name} - {account.number.slice(-4)}
            </div>
          </Row.Item>
        </Row>
      </div>

      <p className={styles.description}>
        {l10n.getString("account-verify-microdeposits")}
      </p>
      {!!uatMicrodepAmntOne && (
        <div className={`message-content success ${styles.uatNotice}`}>
          <span
            className="narmi-icon-alert-circle margin--right--s"
            color="var(--color-successDark)"
          />
          <span className="margin--right--l">
            For the purpose of this demo, please enter the following values:{" "}
            {formatNumber(uatMicrodepAmntOne)} and{" "}
            {formatNumber(uatMicrodepAmntTwo)}.
          </span>
        </div>
      )}
      <ContextForm data={formData} onChange={onChange}>
        <div className={styles.formContainer}>
          <div className={styles.fieldWrapper}>
            <ContextForm.Field required>
              <AmountTextInput
                field="deposit1"
                label={l10n.getString("label-first-amount")}
              />
            </ContextForm.Field>
          </div>
          <ContextForm.Field required>
            <AmountTextInput
              field="deposit2"
              label={l10n.getString("label-second-amount")}
            />
          </ContextForm.Field>
          <div className={styles.buttonRow}>
            <Row alignItems="center" justifyContent="end">
              <Row.Item shrink>
                <ContextForm.Action onSubmit={handleSubmit}>
                  <Button
                    kind="primary"
                    label={l10n.getString("button-verify")}
                  />
                </ContextForm.Action>
              </Row.Item>
            </Row>
          </div>
        </div>
      </ContextForm>
    </>
  );
};

export default VerifyAccount;
